import React from 'react';
import PropTypes from 'prop-types';
import { Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@material-ui/core';
import classNames from 'classnames';
import { withStyles } from '@material-ui/core/styles';
import { getStudentAvatarUrl } from '../../StudentRoster/+store/studentRoster';
import StudentRow from './StudentRow.component';
import { getCurrentYear, getPriorYear } from '../../common/+store/utils';

const isDemo = process.env.REACT_APP_THEME === 'demo';

const styles = (theme) => ({
  row: {
    height: isDemo ? 52 : 75,
    borderBottom: `1px solid ${theme.palette.primary.light}`,
  },
  table: {
    height: '100%',
  },
  content: {
    margin: 'auto',
  },
  textField: {
    width: '280px',
    marginRight: 8,
  },
  tableBody: {
    overflowY: 'scroll',
    width: '100%',
  },
  header: {
    display: 'flex',
  },
  tableHeadFirefox: {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    background: isDemo ? '#E6FBFE' : '#f1fdff',
  },
  tableHeadSafari: {
    position: '-webkit-sticky',
    top: 0,
    zIndex: 1,
    background: isDemo ? '#E6FBFE' : '#f1fdff',
  },
  headCellChrome: {
    fontSize: '1.5rem',
    borderBottom: `1px solid ${theme.palette.primary.main}`,
    padding: 0,
    position: 'sticky',
    top: 0,
    zIndex: 1,
    background: isDemo ? '#E6FBFE' : '#f1fdff',
  },
  headCell: {
    fontSize: '1.5rem',
    borderBottom: `1px solid ${theme.palette.primary.main}`,
    padding: 0,
  },
  largeText: {
    fontSize: '1.5rem',
  },
  smallTextChrome: {
    padding: 0,
    fontSize: '18px',
    borderBottom: `1px solid ${theme.palette.primary.main}`,
    position: 'sticky',
    top: 0,
    zIndex: 1,
    background: isDemo ? '#E6FBFE' : '#f1fdff',
  },
  smallText: {
    padding: 0,
    fontSize: '18px',
    borderBottom: `1px solid ${theme.palette.primary.main}`,
  },
  tableHead: {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    background: isDemo ? '#E6FBFE' : '#f1fdff',
  },
  tableRow: {
    background: isDemo ? '#E6FBFE' : '#f1fdff',
  },
  smallTextEmpty: {
    width: '30px',
    padding: 0,
    fontSize: '18px',
    borderBottom: `1px solid ${theme.palette.primary.main}`,
  },
  smallTextIconChrome: {
    width: '15px',
    padding: 0,
    borderBottom: `1px solid ${theme.palette.primary.main}`,
    position: 'sticky',
    top: 0,
    zIndex: 1,
    background: isDemo ? '#E6FBFE' : '#f1fdff',
  },
  smallTextIcon: {
    width: '15px',
    padding: 0,
    borderBottom: `1px solid ${theme.palette.primary.main}`,
  },
  tableContainer: {
    flex: 1,
    margin: `0 ${theme.spacing.unit * 3}px`,
  },
  button: {
    margin: theme.spacing.unit,
    float: 'right',
  },
  headerSticky: {
    position: 'sticky',
    background: isDemo ? '#E6FBFE' : '#f7feff',
    top: 0,
  },
  headerStickySafari: {
    position: '-webkit-sticky',
  },
  subjectHeader: {
    fontSize: 24,
  },
  yearHeader: {
    fontSize: 18,
  },
  panel: {
    background: '#fff',
    borderRadius: 18,
    overflow: 'hidden',
    boxShadow: '0 8px 28px rgba(58,166,185,0.12)',
    border: '1px solid rgba(191,247,255,0.6)',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
  },
  panelHeader: {
    padding: '14px 22px',
    background: 'linear-gradient(180deg,#f1fcfe,#ffffff)',
    borderBottom: '1px solid #e4eef0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  panelTitle: {
    fontWeight: 700,
    fontSize: '1rem',
    color: '#2c3e40',
  },
  panelBody: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: '56px 24px',
  },
  emptyIcon: {
    color: '#bcd4d8',
    marginBottom: 12,
  },
  emptyHeadline: {
    fontSize: '1rem',
    fontWeight: 600,
    color: '#5d6b6d',
  },
  emptySubtext: {
    fontSize: '0.85rem',
    color: '#9ca3af',
    marginTop: 4,
  },
});

class ProgressGoalsList extends React.PureComponent {
  static propTypes = {
    classes: PropTypes.any.isRequired,
    students: PropTypes.any.isRequired,
    view: PropTypes.any.isRequired,
    avatars: PropTypes.object,
    loading: PropTypes.any,
    onLoadMore: PropTypes.func,
  };

  showMore = (event) => {
    const { students } = this.props;
    const node = event.currentTarget;
    if (students.length && node) {
      node.addEventListener(
        'scroll',
        () => {
          if (node.scrollTop + node.clientHeight >= node.scrollHeight - 70) {
            this.loadMoreItems();
          }
        },
        true
      );
    }
  };

  loadMoreItems() {
    const { onLoadMore, loading } = this.props;
    if (loading) {
      return;
    }
    onLoadMore();
  }

  render() {
    const { students, classes, view, avatars, loading } = this.props;
    const subjectName = {
      math: 'Mathematics',
      reading: 'Reading',
    };
    if (isDemo && !loading && students.length === 0) {
      return (
        <div className={classes.panel}>
          <div className={classes.panelHeader}>
            <Typography className={classes.panelTitle}>Student Progress Goals</Typography>
          </div>
          <div className={classes.emptyState}>
            <svg
              className={classes.emptyIcon}
              xmlns="http://www.w3.org/2000/svg"
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zM7 10h2v7H7v-7zm4-3h2v10h-2V7zm4 6h2v4h-2v-4z" />
            </svg>
            <Typography className={classes.emptyHeadline}>No students to show</Typography>
            <Typography className={classes.emptySubtext}>
              Adjust your filters above to see progress goals for your students.
            </Typography>
          </div>
        </div>
      );
    }
    const table = (
      <div className={classes.tableBody} onScroll={(event) => this.showMore(event)}>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell
                className={classNames(classes.subjectHeader, classes.headerSticky, classes.headerStickySafari)}
                align="left"
              >
                {subjectName.math}
              </TableCell>
              <TableCell
                className={classNames(classes.yearHeader, classes.headerSticky, classes.headerStickySafari)}
                align="center"
              >
                {`${getPriorYear()} Score`}
              </TableCell>
              <TableCell className={classNames(classes.headerSticky, classes.headerStickySafari)} align="center" />
              <TableCell
                className={classNames(classes.yearHeader, classes.headerSticky, classes.headerStickySafari)}
                align="center"
              >
                {`${getCurrentYear()} Goal`}
              </TableCell>
              <TableCell
                className={classNames(classes.subjectHeader, classes.headerSticky, classes.headerStickySafari)}
                align="left"
              >
                {subjectName.reading}
              </TableCell>
              <TableCell
                className={classNames(classes.yearHeader, classes.headerSticky, classes.headerStickySafari)}
                align="center"
              >
                {`${getPriorYear()} Score`}
              </TableCell>
              <TableCell className={classNames(classes.headerSticky, classes.headerStickySafari)} align="center" />
              <TableCell
                className={classNames(classes.yearHeader, classes.headerSticky, classes.headerStickySafari)}
                align="center"
              >
                {`${getCurrentYear()} Goal`}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {students.map(([studentId, studentInfo]) => (
              <TableRow hover key={studentId} className={classes.row}>
                <StudentRow
                  student={studentInfo}
                  view={view}
                  subject="math"
                  avatarUrl={getStudentAvatarUrl(studentInfo, avatars)}
                />
                <StudentRow
                  student={studentInfo}
                  view={view}
                  subject="reading"
                  avatarUrl={getStudentAvatarUrl(studentInfo, avatars)}
                />
              </TableRow>
            ))}
            <TableRow hover className={classes.row}>
              <TableCell key="name" align="left" colSpan={6} className={classes.cell}>
                {loading && <Typography color="primary">Loading more students ...</Typography>}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    );
    if (isDemo) {
      return (
        <div className={classes.panel}>
          <div className={classes.panelHeader}>
            <Typography className={classes.panelTitle}>Student Progress Goals</Typography>
          </div>
          <div className={classes.panelBody}>{table}</div>
        </div>
      );
    }
    return table;
  }
}
export default withStyles(styles)(ProgressGoalsList);
